import ENV from "../env";
import { buildHeaders, handleExpiredLogin } from "./utils";

/** Mirrors the backend's RoadmapSummaryResponse; the roadmaps live in questions/roadmaps/<id>.yml. */
export type RoadmapSummary = {
    id: string;
    title: string;
    cover?: string;
    description?: string;
    courses: number;
    lessons: number;
    /** The roadmaps it imports directly, in order. */
    parents: RoadmapParent[];
};

/** A roadmap another one builds on. */
export type RoadmapParent = {
    id: string;
    title: string;
};

/** Mirrors the backend's RoadmapResponse: the whole tree, which the client filters by level. */
export type Roadmap = {
    id: string;
    title: string;
    cover?: string;
    description?: string;
    levels: RoadmapLevel[];
    root: RoadmapNode;
};

/** One step of the karate ladder: `level` 1 is Kyu 9 (start learning), 12 is Dan 3 (expert). */
export type RoadmapLevel = {
    level: number;
    name: string;
};

/**
 * `NODE`s of any depth come from the roadmap file; a `CHOICE` is a node whose children are alternatives. `LESSON`s
 * are read from the course catalogue under every last node, one per lesson of the groups it references.
 */
export type RoadmapNodeKind = 'ROADMAP' | 'NODE' | 'CHOICE' | 'LESSON';

/** A course, or one lesson group of it, that a node points at; `planned` while the catalogue lacks it. */
export type RoadmapReference = {
    courseId: string;
    groupId?: string;
    title: string;
    planned: boolean;
};

/** From `level` on, `count` alternatives of a choice are expected. */
export type RoadmapPick = {
    level: number;
    count: number;
};

export type RoadmapNode = {
    kind: RoadmapNodeKind;
    /** The node id from the roadmap file, unique among its siblings; the lesson id of a `LESSON`. */
    id?: string;
    /** The course a node links to: of a lesson, or of a node referencing exactly one known course. */
    courseId?: string;
    title: string;
    /** The level the node appears from; a parent appears with its lowest child. */
    level: number;
    /** Whether the course catalogue does not have this node yet. */
    planned: boolean;
    references: RoadmapReference[];
    /** Of a last node only: the knowledge it requires, to compare with the lessons its groups hold. */
    notes: string[];
    /** Of a choice only, ordered by level; empty for every other node. */
    picks: RoadmapPick[];
    children: RoadmapNode[];
};

export class RoadmapService {
    static roadmaps(): Promise<RoadmapSummary[]> {
        return fetch(`${ ENV.BACKEND }/roadmaps`, { method: 'GET', headers: buildHeaders() })
            .then((response) => handleExpiredLogin(response).json());
    }

    /** Rejects on an unknown roadmap so the page can send the user to /404. */
    static roadmap(roadmapId: string): Promise<Roadmap> {
        return fetch(`${ ENV.BACKEND }/roadmaps/${ roadmapId }`, { method: 'GET', headers: buildHeaders() })
            .then((response) => requireFound(handleExpiredLogin(response)).json());
    }
}

function requireFound(response: Response): Response {
    if (!response.ok) {
        throw new Error(`Unable to load roadmap, backend answered ${ response.status }`);
    }

    return response;
}

/** Roadmaps reuse the course covers and fall back to the same neutral one. */
export function roadmapCoverUrl(roadmap: RoadmapSummary): string {
    return `/assets/images/covers/${ roadmap.cover || 'cs-general' }.svg`;
}
